<script setup lang="ts">
import { ref } from 'vue'

import { useNamespace } from '@lit-element/hooks'
import { isFunction } from '@lit-element/utils'
import LtMenu from '@lit-element/components/menu'
import LtTooltip from '@lit-element/components/tooltip'
import menuItem from './menuItem.vue'
import type { ContextmenuConfig, ContextmenuOptions } from './symbol'

defineOptions({ name: 'Contextmenu' })

const ns = useNamespace('contextmenu')
const visible = ref(false)
const configs = ref<ContextmenuConfig[]>([])
const dropdownRef = ref()

const onSelect = ref<((key: string, keyPath: string[]) => void) | null>(null)
const onCancel = ref<(() => void) | null>(null)

const position = ref({
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
})

const triggerRef: any = ref({
  getBoundingClientRect() {
    return position.value
  },
})

function openContextmenu(options: ContextmenuOptions) {
  position.value = DOMRect.fromRect({
    width: 0,
    height: 0,
    x: options.clientX || 0,
    y: options.clientY || 0,
  })

  configs.value = options.configs
  visible.value = true
  return new Promise<{ key: string; keyPath: string[] } | null>((resolve) => {
    onSelect.value = (key: string, keyPath: string[]) => {
      resolve({ key, keyPath })
    }
    onCancel.value = () => {
      resolve(null)
    }
  })
}

function afterContextmenu() {
  onSelect.value = null
  onCancel.value = null
}

const handleSelect = (key: string, keyPath: string[]) => {
  visible.value = false
  if (isFunction(onSelect.value)) {
    onSelect.value(key, keyPath)
    afterContextmenu()
  }
}

function handleCancel() {
  visible.value = false
  if (isFunction(onCancel.value)) {
    onCancel.value()
    afterContextmenu()
  }
}

defineExpose({ visible, position, openContextmenu, handleSelect, handleCancel })
</script>

<template>
  <LtTooltip
    ref="dropdownRef"
    v-model:visible="visible"
    trigger="contextmenu"
    effect="light"
    virtual-triggering
    :virtual-ref="triggerRef"
    :popper-class="[ns.b()]"
    @contextmenu.prevent.stop
  >
    <template #content>
      <lt-menu collapse @select="handleSelect">
        <menuItem :list="configs" :item-comp="menuItem" />
      </lt-menu>
    </template>
  </LtTooltip>
</template>
